import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import type { AppDispatch } from "../../../app/store";
import {
  createHubspotBulkJob,
  fetchHubspotBulkJobs,
  fetchHubspotLists,
  pollHubspotBulkJobs,
  selectHubspotBulkJobs,
  selectHubspotCanVerifyLists,
  selectHubspotConnection,
  selectHubspotCreatingBulkJob,
  selectHubspotLists,
  selectHubspotListsLoaded,
  selectHubspotListsLoading,
} from "../store/hubspotSlice";
import type { HubspotBulkJob, HubspotBulkJobStatus } from "../types";

// A job is still moving through the worker in these states, so the table keeps
// polling while any row is in one of them.
const LIVE_STATUSES: HubspotBulkJobStatus[] = [
  "pending",
  "fetching",
  "queued",
  "writing_back",
];

const STATUS_COPY: Record<HubspotBulkJobStatus, string> = {
  pending: "Starting",
  fetching: "Reading the list",
  queued: "Verifying",
  writing_back: "Writing to HubSpot",
  completed: "Completed",
  failed: "Failed",
};

// While verifying, the badge carries the progress itself - results land on the
// job as they finish, not only at the end.
const statusLabel = (job: HubspotBulkJob) =>
  job.status === "queued" && job.contact_count > 0
    ? `${STATUS_COPY.queued} ${job.verified_count.toLocaleString()} / ${job.contact_count.toLocaleString()}`
    : STATUS_COPY[job.status];

const POLL_INTERVAL_MS = 3000;
const JOBS_PAGE_SIZE = 10;

/**
 * "Verify a HubSpot list" - the bulk flow driven from this side.
 *
 * The whole screen is gated on connection.can_verify_lists rather than on the
 * request failing: a portal connected before the read scopes existed has a
 * perfectly healthy connection that simply cannot read lists, and telling those
 * two apart is the difference between "reconnect" and "something broke".
 */
type CountTooltipVariant = "valid" | "invalid" | "catchall" | "risky";

// The buckets shown against a finished job, in the same order and with the
// same icons as every other verification table in the app. Unknown
// is tracked on the job but not rendered: it only appears when the pipeline
// returns a status the map does not recognise, which is not the common case.
const RESULT_BUCKETS: Array<{
  key: "valid_count" | "invalid_count" | "catch_all_count" | "risky_count";
  label: string;
  variant: CountTooltipVariant;
}> = [
  { key: "valid_count", label: "Valid", variant: "valid" },
  { key: "catch_all_count", label: "Catch-All", variant: "catchall" },
  { key: "risky_count", label: "Risky", variant: "risky" },
  { key: "invalid_count", label: "Invalid", variant: "invalid" },
];

type ResultCountsJob = Pick<
  HubspotBulkJob,
  "verified_count" | "valid_count" | "invalid_count" | "catch_all_count" | "risky_count"
>;

// The Results column of a list-verification jobs table. Shared with the
// Salesforce one, which has the same counts.
export const JobResultCounts: React.FC<{ job: ResultCountsJob }> = ({ job }) => {
  const [countTooltip, setCountTooltip] = useState<{
    top: number;
    left: number;
    text: string;
    variant: CountTooltipVariant;
  } | null>(null);

  const showCountTooltip = (
    e: React.MouseEvent<HTMLDivElement>,
    text: string,
    variant: CountTooltipVariant
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCountTooltip({ top: rect.bottom, left: rect.left + rect.width / 2, text, variant });
  };

  const hideCountTooltip = () => setCountTooltip(null);

  const hasResults =
    job.verified_count > 0 ||
    RESULT_BUCKETS.some((bucket) => (job[bucket.key] ?? 0) > 0);

  // Nothing verified yet. An em dash rather than three zeroes, which would
  // read as "we checked and everything came back empty".
  if (!hasResults) {
    return <span className="muted">&mdash;</span>;
  }

  return (
    <>
      <div className="d-flex flex-row email-verify-details">
        {RESULT_BUCKETS.map((bucket) => (
          <div
            key={bucket.key}
            className="icon-tooltip"
            onMouseEnter={(e) => showCountTooltip(e, bucket.label, bucket.variant)}
            onMouseLeave={hideCountTooltip}
          >
            {bucket.variant === "catchall" ? (
              <svg className="catchall-icon" width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16z" />
                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
              </svg>
            ) : bucket.variant === "risky" ? (
              <svg className="risky-icon" width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-label="Risky">
                <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z" />
                <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
              </svg>
            ) : (
              <img
                className="small-catch"
                src={bucket.variant === "valid" ? "/images/validate.png" : "/images/invalidate.png"}
                alt={bucket.label}
              />
            )}
            <span
              className={
                bucket.variant === "valid"
                  ? "validate ms-1"
                  : bucket.variant === "invalid"
                    ? "invalidate ms-1"
                    : bucket.variant === "catchall"
                      ? "catchall-count ms-1"
                      : "risky-count ms-1"
              }
            >
              {(job[bucket.key] ?? 0).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
      {countTooltip &&
        createPortal(
          <span
            className={`tooltip-text tooltip-portal hubspot-count-tooltip tooltip-${countTooltip.variant}`}
            style={{ top: countTooltip.top, left: countTooltip.left }}
          >
            {countTooltip.text}
          </span>,
          document.body
        )}
    </>
  );
};

// Same pagination as the Verification Logs page; its styles come in with the
// Integration page (email_verify.css). Shared with the Salesforce jobs table.
export const JobsPagination: React.FC<{
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ page, totalPages, onPageChange }) => {
  const [goToPageInput, setGoToPageInput] = useState("");

  if (totalPages <= 1) return null;

  const handlePageChange = (target: number) => {
    if (target >= 1 && target <= totalPages) {
      onPageChange(target);
    }
  };

  const handleGoToPage = () => {
    const target = parseInt(goToPageInput);
    if (!isNaN(target) && target >= 1 && target <= totalPages) {
      handlePageChange(target);
      setGoToPageInput(String(target));
    }
  };

  const renderPageNumbers = () => {
    const pages = [] as (number | string)[];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (page <= 3) {
      pages.push(1, 2, 3, "...", totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", page, "...", totalPages);
    }

    return pages;
  };

  return (
    <div className="pagination-container">
      <button
        className="pagination-arrow"
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1}
      >
        &lt;
      </button>

      {renderPageNumbers().map((item, index) =>
        item === "..." ? (
          <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
        ) : (
          <button
            key={item}
            className={`pagination-number ${page === item ? "active" : ""}`}
            onClick={() => handlePageChange(item as number)}
          >
            {item}
          </button>
        )
      )}
      <button
        className="pagination-arrow"
        onClick={() => handlePageChange(page + 1)}
        disabled={page === totalPages}
      >
        &gt;
      </button>

      <div className="go-to-page">
        <span>Go to page</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={goToPageInput}
          onChange={(e) => setGoToPageInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleGoToPage();
            }
          }}
          onBlur={handleGoToPage}
          className="page-input"
        />
      </div>
    </div>
  );
};

const HubspotListVerifier: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const connection = useSelector(selectHubspotConnection);
  const canVerifyLists = useSelector(selectHubspotCanVerifyLists);
  const lists = useSelector(selectHubspotLists);
  const listsLoading = useSelector(selectHubspotListsLoading);
  const listsLoaded = useSelector(selectHubspotListsLoaded);
  const jobs = useSelector(selectHubspotBulkJobs);
  const creating = useSelector(selectHubspotCreatingBulkJob);

  const [selectedListId, setSelectedListId] = useState("");
  const [listSearch, setListSearch] = useState("");
  const [jobsPage, setJobsPage] = useState(1);

  useEffect(() => {
    if (!canVerifyLists) return;
    // The endpoint's maximum, and no total comes back - the table pages
    // through these client-side.
    dispatch(fetchHubspotBulkJobs({ limit: 100 }));
  }, [dispatch, canVerifyLists]);

  // HubSpot does the matching, not this component: the picker is capped at 500
  // lists, so filtering what already arrived would leave a bigger portal unable
  // to reach the rest. The first load is immediate; only typing waits.
  useEffect(() => {
    if (!canVerifyLists) return;
    const term = listSearch.trim();
    const timer = setTimeout(() => {
      dispatch(fetchHubspotLists(term));
    }, term ? 350 : 0);
    return () => clearTimeout(timer);
  }, [dispatch, canVerifyLists, listSearch]);

  // A search that no longer returns the chosen list would otherwise leave the
  // id selected with nothing behind it, and Verify list would quietly do nothing.
  useEffect(() => {
    if (selectedListId && !lists.some((item) => item.list_id === selectedListId)) {
      setSelectedListId("");
    }
  }, [lists, selectedListId]);

  // Only poll while something is actually in flight. A table of finished jobs
  // never changes on its own, and a permanent timer against a live API is not
  // worth paying for a screen nobody is watching.
  const hasLiveJob = jobs.some((job) => LIVE_STATUSES.includes(job.status));
  useEffect(() => {
    if (!canVerifyLists || !hasLiveJob) return;
    const timer = window.setInterval(() => {
      dispatch(pollHubspotBulkJobs({ limit: JOBS_PAGE_SIZE }));
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [dispatch, canVerifyLists, hasLiveJob]);

  if (!connection) return null;

  if (!canVerifyLists) {
    return (
      <div className="alert alert-info d-flex" role="status">
        <i className="fas fa-circle-info me-3 mt-1" aria-hidden="true" />
        <div>
          <div className="fw-semibold mb-1">Reconnect to verify whole lists</div>
          <p className="mb-0">
            This portal was connected before list verification was available, and HubSpot cannot add
            permissions to a connection that already exists. Disconnect and connect again to grant
            read access, and you will be able to verify a whole HubSpot list from here. Your existing{" "}
            <strong>Verify Email with ReachStream</strong> workflow step keeps working either way.
          </p>
        </div>
      </div>
    );
  }

  const selectedList = lists.find((item) => item.list_id === selectedListId);

  const jobsTotalPages = Math.max(1, Math.ceil(jobs.length / JOBS_PAGE_SIZE));
  const jobsPageShown = Math.min(jobsPage, jobsTotalPages);
  const pagedJobs = jobs.slice((jobsPageShown - 1) * JOBS_PAGE_SIZE, jobsPageShown * JOBS_PAGE_SIZE);

  const handleVerify = async () => {
    if (!selectedList) return;
    const action = await dispatch(
      createHubspotBulkJob({ list_id: selectedList.list_id, list_name: selectedList.name })
    );
    if (createHubspotBulkJob.fulfilled.match(action)) {
      setSelectedListId("");
      // The new job lands at the top of page 1.
      setJobsPage(1);
      toast.success(
        `Verifying "${selectedList.name}". Results are written onto the contacts as they finish.`
      );
    }
  };

  return (
    <div className="hubspot-lists">
      <div className="hubspot-log-head">Verify a HubSpot list</div>

      <p className="hubspot-lists-desc">
        Pick a contact list and ReachStream verifies every address in it, then writes the result onto
        each contact&apos;s <strong>RS Email Verifier Status</strong> property.
      </p>

      <div className="hubspot-lists-picker">
        <input
          type="search"
          className="hubspot-lists-search"
          value={listSearch}
          onChange={(event) => setListSearch(event.target.value)}
          placeholder="Search lists by name..."
          disabled={creating}
          aria-label="Search HubSpot contact lists"
        />

        <select
          className="hubspot-lists-select"
          value={selectedListId}
          onChange={(event) => setSelectedListId(event.target.value)}
          disabled={listsLoading || creating || lists.length === 0}
          aria-label="HubSpot contact list"
        >
          <option value="">
            {listsLoading
              ? "Loading your lists..."
              : lists.length === 0
              ? listSearch.trim()
                ? "No lists match that search"
                : "No contact lists found in this portal"
              : "Choose a list..."}
          </option>
          {lists.map((item) => (
            <option key={item.list_id} value={item.list_id}>
              {item.name}
              {item.size !== null ? ` (${item.size.toLocaleString()} contacts)` : ""}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="connect-btn"
          onClick={handleVerify}
          disabled={!selectedListId || creating}
        >
          {creating ? "Starting..." : "Verify list"}
        </button>
      </div>

      {selectedList && (
        <p className="hubspot-lists-hint">
          {selectedList.size !== null
            ? `About ${selectedList.size.toLocaleString()} contacts. `
            : ""}
          One credit is charged per email address - contacts without one, and malformed addresses, are free.
        </p>
      )}

      {listsLoaded && lists.length === 0 && !listSearch.trim() && (
        <p className="hubspot-lists-hint">
          Create a list in HubSpot under <strong>Contacts &rarr; Lists</strong>, then come back here.
        </p>
      )}

      {jobs.length > 0 && (
        <table className="hubspot-log-table">
          <thead>
            <tr>
              <th>List</th>
              <th>Status</th>
              <th>Contacts</th>
              <th>Results</th>
              <th>Written back</th>
              <th>Started</th>
            </tr>
          </thead>
          <tbody>
            {pagedJobs.map((job) => (
              <tr key={job.id}>
                <td>{job.hubspot_list_name || `List ${job.hubspot_list_id}`}</td>
                <td>
                  <span className={`verdict-badge ${job.status}`}>{statusLabel(job)}</span>
                  {job.status === "failed" && job.error_message && (
                    <div className="hubspot-lists-error">{job.error_message}</div>
                  )}
                </td>
                <td className="muted">{job.contact_count.toLocaleString()}</td>
                <td>
                  <JobResultCounts job={job} />
                </td>
                <td className="muted">
                  {job.written_count.toLocaleString()}
                  {job.verified_count > 0 ? ` / ${job.verified_count.toLocaleString()}` : ""}
                </td>
                <td className="muted">{new Date(job.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <JobsPagination page={jobsPageShown} totalPages={jobsTotalPages} onPageChange={setJobsPage} />

    </div>
  );
};

export default HubspotListVerifier;
